import { bigIntToUint8Array, concatBuffers, sortUint8ArraysLex } from "../commons/cryptoUtils";

/**
 * Implementazione PeerPairingClient V2
 * - ECDH P-521
 * - HKDF-SHA256
 * - Symmetric Encryption AES-256-GCM (authenticated encryption)
 * - HKDF salt = "GOT2-KeyExchange-Saltv2" (UTF-8 bytes); info = "GOT2-E2EE-v2" + channel + sorted public keys; output 48 byte (32 key + 16 IV)
 * - IV/Nonce Random 12-byte nonce per encryption (GCM standard)
 * - Authentication 16-byte GCM authentication tag
 * - SAS code 6-digit code for MITM detection
 * - Key confirmation HMAC-SHA256 digest (prepared, server pending)
 */
export class PeerPairingClientV2 {
    private readonly ec: any;
    private readonly keyPair: any;

    constructor() {
        const EC = require("elliptic").ec;
        this.ec = new EC("p521");
        this.keyPair = this.ec.genKeyPair();
    }

    /**
     * Compressed public key (67 bytes)
     */
    getCompressedPublicKey(): Uint8Array {
        return new Uint8Array(this.keyPair.getPublic(true, "array"));
    }

    /**
     * Deriva encryptionKey + confirmationKey + sharedSecret
     */
    async deriveAESParameters(
        peerPublicKeyBytes: Uint8Array,
        channel: string
    ): Promise<{
        encryptionKey: Uint8Array;
        confirmationKey: Uint8Array;
        sharedSecret: Uint8Array;
    }> {

        // Importa chiave pubblica peer (compressed) e calcola shared secret ECDH
        const peerKey = this.ec.keyFromPublic(peerPublicKeyBytes, "array");

        // sharedSecretBN è un BN (big number) che rappresenta il punto x della curva risultante da ECDH
        const sharedSecretBN = this.keyPair.derive(peerKey.getPublic());
        // Converte il BN in un Uint8Array di lunghezza fissa (66 byte per P-521)
        const sharedSecret = bigIntToUint8Array(sharedSecretBN, 66);
        // Ora sharedSecret è un Uint8Array di 66 byte che rappresenta il risultato di ECDH
        const localPub = this.getCompressedPublicKey();

        // CRITICAL: lexicographic unsigned sort (browser-safe)
        // Ordina le chiavi pubbliche (local e peer) in modo lessicografico (unsigned) 
        // per garantire che entrambe le parti derivino la stessa chiave indipendentemente dall'ordine di input
        const sortedKeys = sortUint8ArraysLex(localPub, peerPublicKeyBytes);

        // Prepara info per HKDF: "GOT2-E2EE-v2" || channel || sortedPubKey1 || sortedPubKey2
        const info: Uint8Array = concatBuffers(
            new TextEncoder().encode("GOT2-E2EE-v2"),
            new TextEncoder().encode(channel),
            sortedKeys[0],
            sortedKeys[1]
        );

        // Deriva 64 byte di chiave usando HKDF-SHA256 con il shared secret come input
        const hkdfKey: CryptoKey = await crypto.subtle.importKey(
            "raw",
            sharedSecret as BufferSource,
            "HKDF",
            false,
            ["deriveBits"]
        );

        // Deriva 64 byte: primi 32 byte per encryptionKey, successivi 32 byte per confirmationKey
        const derivedBits = await crypto.subtle.deriveBits(
            {
                name: "HKDF",
                hash: "SHA-256",
                salt: new TextEncoder().encode("GOT2-KeyExchange-Salt-v2"),
                info: info as BufferSource,
            },
            hkdfKey,
            64 * 8 // 64 bytes
        );

        // Converte i bits derivati in un Uint8Array
        const derived = new Uint8Array(derivedBits);

        // Restituisce encryptionKey (32 byte), confirmationKey (32 byte) e sharedSecret (66 byte)
        return {
            encryptionKey: derived.slice(0, 32),
            confirmationKey: derived.slice(32, 64),
            sharedSecret,
        };
    }

    /**
     * SAS 6-digit (little-endian obbligatorio)
     */
    async generateSasCode(
        sharedSecret: Uint8Array,
        channel: string,
        peerPublicKeyBytes: Uint8Array
    ): Promise<string> {

        // Ottiene chiave pubblica compressa locale per info HKDF
        const localPub = this.getCompressedPublicKey();

        // CRITICAL: lexicographic unsigned sort (browser-safe) per garantire che entrambe le parti 
        // generino lo stesso SAS indipendentemente dall'ordine di input
        const sortedKeys = sortUint8ArraysLex(localPub, peerPublicKeyBytes);

        // Prepara info per HKDF: "GOT2-SAS-v2" || channel || sortedPubKey1 || sortedPubKey2
        const info = concatBuffers(
            new TextEncoder().encode("GOT2-SAS-v2"),
            new TextEncoder().encode(channel),
            sortedKeys[0],
            sortedKeys[1]
        );

        // Deriva 4 byte usando HKDF-SHA256 con il shared secret come input
        const hkdfKey = await crypto.subtle.importKey(
            "raw",
            sharedSecret as BufferSource,
            "HKDF",
            false,
            ["deriveBits"]
        );

        // Deriva 4 byte (32 bit) per generare il codice SAS
        const derivedBits = await crypto.subtle.deriveBits(
            {
                name: "HKDF",
                hash: "SHA-256",
                salt: new TextEncoder().encode("GOT2-SAS-Salt"),
                info: info as BufferSource,
            },
            hkdfKey,
            4 * 8
        );

        // Converte i bits derivati in un Uint8Array
        const sasBytes = new Uint8Array(derivedBits);

        // Interpreta i 4 byte come un numero intero unsigned in little-endian
        const view = new DataView(sasBytes.buffer);
        // Ottiene un numero a 32 bit (unsigned) dal DataView in little-endian
        const number = view.getUint32(0, true); // little-endian

        // Restituisce il numero modulo 1 milione, formattato come stringa di 6 cifre con zero-padding
        return (number % 1_000_000).toString().padStart(6, "0");
    }

    /**
     * Key Confirmation HMAC-SHA256
     */
    async computeKeyConfirmation(
        liveKitKey: Uint8Array,
        confirmationKey: Uint8Array
    ): Promise<Uint8Array> {

        // Importa confirmationKey come chiave HMAC
        const key = await crypto.subtle.importKey(
            "raw",
            confirmationKey as BufferSource,
            { name: "HMAC", hash: "SHA-256" },
            false,
            ["sign"]
        );

        // Calcola digest HMAC-SHA256 del liveKitKey usando confirmationKey come chiave
        const digest = await crypto.subtle.sign(
            "HMAC",
            key,
            liveKitKey as BufferSource
        );

        return new Uint8Array(digest);
    }

}